import { File01Icon, Store01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { api } from "@slapinc/slappos-backend/api";
import {
	createFileRoute,
	useNavigate,
	useSearch,
} from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import type { GenericId as Id } from "convex/values";
import { useCallback, useState } from "react";
import { BranchSelector } from "@/components/branch-selector";
import { Can } from "@/components/can";
import { PageContent } from "@/components/layout/page-content";
import { CreatePrescriptionModal } from "@/components/modals/create-prescription-modal";
import { ActionHeaderButton } from "@/components/ui/action-header-button";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { EmptyStateWithSkeleton } from "@/components/ui/empty-state-with-skeleton";
import { PrescriptionsTable } from "@/features/prescriptions/_components/prescriptions-table";
import { useAutoSelectBranch } from "@/hooks/use-auto-select-branch";
import { useMe } from "@/hooks/use-me";
import { handleConvexError } from "@/lib/errors";
import type { PrescriptionInput } from "@/lib/schemas/prescription";
import { toast } from "@/lib/toast";
import type { Prescription } from "@/lib/types/prescription";

interface PrescriptionsSearch {
	branch?: string;
}

export const Route = createFileRoute("/(dashboard)/branch/prescriptions")({
	component: PrescriptionsPage,
	validateSearch: (search: Record<string, unknown>) => ({
		branch: search.branch as string | undefined,
	}),
});

function PrescriptionsPage() {
	const navigate = useNavigate();
	const { branch: searchBranchId } = useSearch({
		from: "/(dashboard)/branch/prescriptions",
	});

	const [branchSelectorOpen, setBranchSelectorOpen] = useState(false);
	const [createModalOpen, setCreateModalOpen] = useState(false);
	const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
	const [prescriptionToCancel, setPrescriptionToCancel] =
		useState<Prescription | null>(null);
	const [isCreating, setIsCreating] = useState(false);
	const [isCancelling, setIsCancelling] = useState(false);

	const me = useMe();
	const orgId = me?.activeOrganizationId;

	const branchesData = useQuery(
		api.features.branches.web.list,
		orgId ? { orgId } : "skip",
	);
	const branches = branchesData ?? [];

	const { branchId, isLoading: isLoadingBranches } = useAutoSelectBranch({
		branchId: searchBranchId,
		branches: branchesData,
		orgId,
	});

	const prescriptionsData = useQuery(
		api.features.prescriptions.web.list,
		orgId && branchId
			? { orgId, branchId: branchId as Id<"branches"> }
			: "skip",
	);

	const customersData = useQuery(
		api.features.customers.web.list,
		orgId ? { orgId } : "skip",
	);

	const prescribersData = useQuery(
		api.features.prescribers.web.list,
		orgId ? { orgId } : "skip",
	);

	const createPrescription = useMutation(api.features.prescriptions.web.create);
	const cancelPrescription = useMutation(api.features.prescriptions.web.cancel);

	const handleBranchChange = useCallback(
		(value: Id<"branches"> | Id<"branches">[] | undefined) => {
			if (typeof value !== "string") return;
			navigate({
				to: ".",
				search: (prev: PrescriptionsSearch) => ({ ...prev, branch: value }),
			});
		},
		[navigate],
	);

	const handleCreate = useCallback(
		async (data: PrescriptionInput) => {
			if (!orgId || !branchId) return;
			setIsCreating(true);
			try {
				await createPrescription({
					orgId,
					branchId: branchId as Id<"branches">,
					customerId: data.customerId as Id<"customers">,
					prescriberId: data.prescriberId as Id<"prescribers">,
					dailyDosage: data.dailyDosage,
					prescriptionDays: data.prescriptionDays,
					totalPrescriptionAmount: data.totalPrescriptionAmount,
					expiryDate: data.expiryDate || undefined,
				});
				toast.success(<>Prescription created</>);
			} catch (error) {
				handleConvexError(error, "Failed to create prescription");
				throw error;
			} finally {
				setIsCreating(false);
			}
		},
		[orgId, branchId, createPrescription],
	);

	const handleCancelClick = useCallback((prescription: Prescription) => {
		setPrescriptionToCancel(prescription);
		setCancelDialogOpen(true);
	}, []);

	const confirmCancel = useCallback(async () => {
		if (!orgId || !prescriptionToCancel) return;
		setIsCancelling(true);
		try {
			await cancelPrescription({
				orgId,
				prescriptionId: prescriptionToCancel._id,
			});
			toast.success(<>Prescription cancelled</>);
			setCancelDialogOpen(false);
			setPrescriptionToCancel(null);
		} catch (error) {
			handleConvexError(error, "Failed to cancel prescription");
		} finally {
			setIsCancelling(false);
		}
	}, [orgId, prescriptionToCancel, cancelPrescription]);

	const prescriptions = prescriptionsData ?? [];
	const isLoading =
		isLoadingBranches || (prescriptionsData === undefined && !!branchId);
	const customers = customersData?.page ?? [];
	const prescribers = prescribersData ?? [];

	function renderContent() {
		if (!branchId && !isLoadingBranches) {
			return (
				<div className="flex flex-1 flex-col justify-end pb-12">
					<EmptyStateWithSkeleton
						title="No branches found"
						description="Get started by creating your first branch to view prescriptions."
						actionLabel="Create branch"
						onAction={() => navigate({ to: "/settings/branches" })}
						icon={
							<HugeiconsIcon
								icon={Store01Icon}
								className="h-5 w-5 text-neutral-700"
								strokeWidth={2.2}
							/>
						}
					/>
				</div>
			);
		}
		if (!isLoading && prescriptions.length === 0) {
			return (
				<div className="flex flex-1 flex-col justify-end pb-12">
					<EmptyStateWithSkeleton
						title="No prescriptions found"
						description="Prescriptions you scan, upload, or add manually will appear here."
						actionLabel="Add Prescription"
						onAction={() => setCreateModalOpen(true)}
						icon={
							<HugeiconsIcon
								icon={File01Icon}
								className="h-5 w-5 text-neutral-700"
								strokeWidth={2.2}
							/>
						}
					/>
				</div>
			);
		}
		return (
			<PrescriptionsTable
				data={prescriptions}
				loading={isLoading}
				onCancel={handleCancelClick}
			/>
		);
	}

	return (
		<PageContent
			title="Prescriptions"
			controls={
				<div className="flex items-center gap-2">
					<BranchSelector
						branches={branches}
						value={branchId as Id<"branches"> | undefined}
						onValueChange={handleBranchChange}
						placeholder="Select branch"
						className="w-48"
						open={branchSelectorOpen}
						onOpenChange={setBranchSelectorOpen}
						onViewAll={() => navigate({ to: "/settings/branches" })}
						onCreateNew={() => navigate({ to: "/settings/branches" })}
					/>
					{branchId && (
						<Can scope="prescriptions.create">
							<ActionHeaderButton
								label="Add Prescription"
								onClick={() => setCreateModalOpen(true)}
							/>
						</Can>
					)}
				</div>
			}
		>
			<div className="flex flex-1 flex-col gap-4 sm:gap-6">
				{renderContent()}
			</div>

			<CreatePrescriptionModal
				open={createModalOpen}
				onOpenChange={setCreateModalOpen}
				customers={customers}
				prescribers={prescribers}
				onSubmit={handleCreate}
				isSubmitting={isCreating}
			/>

			<AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Cancel prescription?</AlertDialogTitle>
						<AlertDialogDescription>
							Are you sure you want to cancel prescription "
							{prescriptionToCancel?.prescriptionNo}"? This cannot be undone.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isCancelling}>
							Keep prescription
						</AlertDialogCancel>
						<AlertDialogAction
							variant="destructive"
							onClick={confirmCancel}
							disabled={isCancelling}
						>
							{isCancelling ? "Cancelling..." : "Cancel prescription"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</PageContent>
	);
}
